/**
 * ScoreSystem - Handles scoring logic for gameplay
 *
 * Features:
 * - Tracks score from enemy kills
 * - Calculates gate bonus points
 * - Calculates army bonus at level end
 * - Integrates with SaveSystem for high scores and stats
 *
 * Per PRD section 3.3:
 * - FR-017: Points awarded for each enemy killed
 * - FR-018: Bonus points for gate multipliers
 * - FR-019: Bonus points for soldiers remaining at level end
 * - FR-020: High score tracked per level
 * - FR-021: Total stats tracked
 */

import { SCORING } from '../utils/constants';
import { saveSystem } from './SaveSystem';

/**
 * Breakdown of score components for display on results screen
 */
export interface ScoreBreakdown {
  /** Points from killing enemies */
  kills: number;
  /** Bonus points from passing through gates */
  gateBonus: number;
  /** Bonus points for soldiers remaining at level end */
  armyBonus: number;
  /** Total score (kills + gateBonus + armyBonus) */
  total: number;
}

/**
 * Result of finalizing a level's score
 */
export interface LevelScoreResult {
  /** Full score breakdown */
  breakdown: ScoreBreakdown;
  /** Whether this score beat the previous high score */
  isNewHighScore: boolean;
  /** The previous high score for comparison */
  previousHighScore: number;
}

/**
 * ScoreSystem tracks and calculates all scoring during gameplay
 *
 * Usage:
 *   scoreSystem.setLevel(1, 1); // Set level/tier context
 *   scoreSystem.addKillPoints(10); // When enemy dies
 *   scoreSystem.addGateBonus(3, 20); // When passing through 3x gate, gained 20 soldiers
 *   const result = scoreSystem.finalizeLevelScore(45); // At level end with 45 soldiers
 */
export class ScoreSystem {
  // Score components
  private killsScore: number = 0;
  private gateBonus: number = 0;

  // Tracking for stats
  private enemyKillCount: number = 0;
  private soldiersCollected: number = 0;

  // Level context
  private currentLevel: number = 1;
  private currentTier: number = 1;

  /**
   * Resets score tracking for a new level attempt
   */
  reset(): void {
    this.killsScore = 0;
    this.gateBonus = 0;
    this.enemyKillCount = 0;
    this.soldiersCollected = 0;
  }

  /**
   * Sets the current level and tier context
   * Call this at the start of each level
   */
  setLevel(level: number, tier: number = 1): void {
    this.currentLevel = level;
    this.currentTier = tier;
    this.reset();
  }

  /**
   * Gets the current level number
   */
  getCurrentLevel(): number {
    return this.currentLevel;
  }

  /**
   * Gets the current tier
   */
  getCurrentTier(): number {
    return this.currentTier;
  }

  // ===========================================================================
  // SCORING ACTIONS
  // ===========================================================================

  /**
   * Adds points for killing an enemy
   * Basic enemy: 10 points (SCORING.basicEnemyPoints)
   * Elite enemy: HP × 10 points (SCORING.elitePointsMultiplier)
   */
  addKillPoints(points: number): void {
    this.killsScore += points;
    this.enemyKillCount++;
  }

  /**
   * Adds bonus points for passing through a gate
   * Bonus formula: 50 base points × gate multiplier
   * Also tracks soldiers collected for stats
   *
   * @param multiplier - The gate's multiplier value (2, 3, or 5)
   * @param soldiersGained - Number of soldiers added from the gate
   */
  addGateBonus(multiplier: number, soldiersGained: number): void {
    // Gate bonus: base 50 points × multiplier
    // 2x gate = 100 points, 3x gate = 150 points, 5x gate = 250 points
    const bonus = 50 * multiplier;
    this.gateBonus += bonus;
    this.soldiersCollected += soldiersGained;
  }

  // ===========================================================================
  // SCORE RETRIEVAL
  // ===========================================================================

  /**
   * Gets the current running score (kills + gates)
   * Does NOT include army bonus (that's only at level end)
   */
  getCurrentScore(): number {
    return this.killsScore + this.gateBonus;
  }

  /**
   * Gets the kill points component
   */
  getKillsScore(): number {
    return this.killsScore;
  }

  /**
   * Gets the gate bonus component
   */
  getGateBonusScore(): number {
    return this.gateBonus;
  }

  /**
   * Gets current score breakdown (without army bonus)
   */
  getBreakdown(): ScoreBreakdown {
    return {
      kills: this.killsScore,
      gateBonus: this.gateBonus,
      armyBonus: 0, // Only calculated at level end
      total: this.getCurrentScore(),
    };
  }

  /**
   * Gets enemy kill count for current level (for stats)
   */
  getEnemyKillCount(): number {
    return this.enemyKillCount;
  }

  /**
   * Gets soldiers collected count for current level (for stats)
   */
  getSoldiersCollected(): number {
    return this.soldiersCollected;
  }

  // ===========================================================================
  // LEVEL COMPLETION
  // ===========================================================================

  /**
   * Finalizes the score when a level is completed successfully
   * Calculates army bonus, checks for high score, and updates stats
   *
   * @param soldiersRemaining - Number of soldiers alive at level end
   * @returns Full result with breakdown, high score status, and previous high
   */
  finalizeLevelScore(soldiersRemaining: number): LevelScoreResult {
    // Calculate army bonus (5 points per surviving soldier)
    const armyBonus = soldiersRemaining * SCORING.soldierBonusPoints;

    const breakdown: ScoreBreakdown = {
      kills: this.killsScore,
      gateBonus: this.gateBonus,
      armyBonus,
      total: this.killsScore + this.gateBonus + armyBonus,
    };

    // Get previous high score before saving
    const previousHighScore = saveSystem.getLevelScore(
      this.currentLevel,
      this.currentTier
    );

    // Save score (SaveSystem only saves if it's a new high)
    const isNewHighScore = saveSystem.setLevelScore(
      this.currentLevel,
      breakdown.total,
      this.currentTier
    );

    // Update lifetime stats
    saveSystem.updateStats({
      totalKills: this.enemyKillCount,
      totalSoldiersCollected: this.soldiersCollected,
    });

    return {
      breakdown,
      isNewHighScore,
      previousHighScore,
    };
  }

  /**
   * Records a level completion in stats
   * Call this after finalizeLevelScore for successful completions
   */
  recordLevelComplete(): void {
    saveSystem.updateStats({
      totalLevelsCompleted: 1,
    });
  }

  /**
   * Calculates what the army bonus would be for a given soldier count
   * Useful for previewing score before level end
   */
  calculateArmyBonus(soldiers: number): number {
    return soldiers * SCORING.soldierBonusPoints;
  }

  // ===========================================================================
  // HIGH SCORE UTILITIES
  // ===========================================================================

  /**
   * Gets the high score for a specific level
   * Delegates to SaveSystem
   */
  getHighScore(level: number, tier?: number): number {
    return saveSystem.getLevelScore(level, tier);
  }

  /**
   * Gets all high scores for the current tier
   * Returns record like { "1": 1500, "2": 2300, ... }
   */
  getAllHighScores(): Record<string, number> {
    const allScores = saveSystem.getAllScores();
    const tierPrefix = `${this.currentTier}-`;

    // Filter to current tier and strip tier prefix
    const tierScores: Record<string, number> = {};
    for (const [key, score] of Object.entries(allScores)) {
      if (key.startsWith(tierPrefix)) {
        const level = key.substring(tierPrefix.length);
        tierScores[level] = score;
      }
    }

    return tierScores;
  }
}

// Export singleton instance for easy access
export const scoreSystem = new ScoreSystem();
